import { useMemo, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { BookOpen, Search } from 'lucide-react'
import {
  canSeeSection,
  docs,
  docsBySection,
  getDoc,
  searchDocs,
  SECTION_META,
  type Doc,
} from '../lib/docs'
import { DocViewer } from '../components/DocViewer'
import { useAuth } from '../context/AuthContext'

export default function KnowledgeBase() {
  const { section, slug } = useParams()
  const activeDoc = section && slug ? getDoc(section, slug) : undefined
  const [query, setQuery] = useState('')
  const { user, can } = useAuth()
  const isEngineerView = can(['super_admin'])
  const role = user?.role

  // Drop entire sections the current role isn't entitled to see.
  // Note: deep-linking to a doc in a hidden section still resolves (the doc
  // viewer doesn't gate), but it won't appear in the sidebar or search.
  const grouped = useMemo(
    () => docsBySection().filter(g => canSeeSection(g.section, role)),
    [role],
  )
  const visibleDocCount = useMemo(
    () => grouped.reduce((sum, g) => sum + g.items.length, 0),
    [grouped],
  )
  const searchResults = useMemo(
    () => (query.trim() ? searchDocs(query).filter(d => canSeeSection(d.section, role)) : []),
    [query, role],
  )
  const searchActive = query.trim().length > 0

  return (
    <div className="flex h-full bg-[#111]">
      <aside className="w-72 flex-shrink-0 border-r border-[#2a2a2a] bg-[#161616] flex flex-col">
        <div className="p-4 border-b border-[#2a2a2a]">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={16} className="text-[#C41E3A]" />
            <h2 className="text-sm font-semibold text-white">Knowledge Base</h2>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full pl-9 pr-3 py-2 bg-[#0f0f0f] border border-[#2a2a2a] rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#C41E3A]"
            />
          </div>
          <p className="text-[10px] text-gray-500 mt-2">
            {searchActive
              ? `${searchResults.length} match${searchResults.length === 1 ? '' : 'es'}`
              : `${visibleDocCount} of ${docs.length} docs · ${grouped.length} sections`}
          </p>
          <p className="text-[10px] text-gray-600 mt-1">
            {isEngineerView ? 'Engineering view' : 'Operator view'}
          </p>
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          {searchActive ? (
            <SearchList results={searchResults} />
          ) : (
            grouped.map(g => <SectionGroup key={g.section} section={g.section} label={g.label} items={g.items} />)
          )}
        </nav>
      </aside>

      <div className="flex-1 overflow-y-auto">
        {activeDoc ? <DocViewer key={activeDoc.slug} doc={activeDoc} /> : <KbIndex grouped={grouped} />}
      </div>
    </div>
  )
}

function SectionGroup({ section, label, items }: { section: string; label: string; items: Doc[] }) {
  return (
    <div className="mb-4">
      <p className="text-[10px] text-gray-500 uppercase tracking-widest px-3 mb-1.5">{label}</p>
      <div className="space-y-0.5">
        {items.map(d => (
          <NavLink
            key={d.slug}
            to={`/kb/${d.slug}`}
            className={({ isActive }) =>
              `block px-3 py-1.5 rounded text-sm leading-snug transition-colors ${
                isActive
                  ? 'bg-[#C41E3A]/15 text-[#C41E3A] font-medium'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`
            }
            title={d.frontmatter.title}
          >
            <span className="block truncate">{d.frontmatter.title}</span>
            {d.frontmatter.status !== 'stable' && (
              <span className="block text-[10px] text-amber-400/70 mt-0.5">{d.frontmatter.status}</span>
            )}
          </NavLink>
        ))}
      </div>
      <p className="text-[10px] text-gray-600 px-3 mt-1">{section}/</p>
    </div>
  )
}

function SearchList({ results }: { results: Doc[] }) {
  if (results.length === 0) {
    return <p className="px-3 py-4 text-sm text-gray-500">No matches.</p>
  }
  return (
    <div className="space-y-0.5">
      {results.map(d => (
        <NavLink
          key={d.slug}
          to={`/kb/${d.slug}`}
          className={({ isActive }) =>
            `block px-3 py-2 rounded text-sm transition-colors ${
              isActive
                ? 'bg-[#C41E3A]/15 text-[#C41E3A]'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`
          }
        >
          <span className="block truncate">{d.frontmatter.title}</span>
          <span className="block text-[10px] text-gray-600 mt-0.5">{d.section}/{d.filename}</span>
        </NavLink>
      ))}
    </div>
  )
}

// Renders the landing-card grid when no specific doc is open. Receives the
// already-role-filtered `grouped` from the parent so we don't duplicate the
// filtering logic (and don't accidentally show sections the user shouldn't
// see — earlier this component called `docsBySection()` itself, which
// bypassed the role gate).
function KbIndex({ grouped }: { grouped: ReturnType<typeof docsBySection> }) {
  return (
    <div className="max-w-4xl mx-auto py-12 px-8">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <BookOpen size={28} className="text-[#C41E3A]" />
          <h1 className="text-3xl font-bold text-white">DrinX Knowledge Base</h1>
        </div>
        <p className="text-gray-400 leading-relaxed max-w-2xl">
          Internal documentation for everyone working on DrinX — operators, support, dev. Step-by-step guides
          for every screen across the customer app, ambassador app, admin portal, and the shared policies that
          tie them together.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {grouped.map(g => (
          <div key={g.section} className="border border-[#2a2a2a] rounded-lg bg-[#161616] p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white">{g.label}</h2>
              <span className="text-xs text-gray-500">{g.items.length} doc{g.items.length === 1 ? '' : 's'}</span>
            </div>
            <ul className="space-y-1.5">
              {g.items.slice(0, 6).map(d => (
                <li key={d.slug}>
                  <NavLink to={`/kb/${d.slug}`} className="text-sm text-gray-400 hover:text-[#C41E3A] transition-colors">
                    {d.frontmatter.title}
                  </NavLink>
                </li>
              ))}
              {g.items.length > 6 && (
                <li>
                  <span className="text-xs text-gray-500">…and {g.items.length - 6} more</span>
                </li>
              )}
            </ul>
            <p className="text-[10px] text-gray-600 mt-3 uppercase tracking-widest">{SECTION_META[g.section].label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
